import React, { useMemo, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useLocation, useParams } from 'react-router-dom';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';
import useHashmapConverter from 'hooks/useHashmapConverter';

import { useGlobalFilterDataStore } from 'store/globalFilterData.store';
import { useProjectTreeStore } from 'store/projectContract.store';
import { useVersionStore } from 'store/version.store';

import { QueryParams, useMutationCreateSegment } from 'api/mutations/createSegment.mutation';
import { useMutationUpdateSegment } from 'api/mutations/updateSegment.mutation';
import { useQueryGetSegmentsList } from 'api/queries/getSegmentsList.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

import { createSpecialFiltersPayload } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoard.utils';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/components/ActivitiesSegments/components/SaveSegment/SaveSegment.module.scss';
import { generateFilterString } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/components/ActivitiesSegments/components/SaveSegment/SaveSegment.utils';
import { ProgramStatusUrlParams } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramStatusSummaryReport/programStatusSummaryReportTypes';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { Radio } from 'components/DesignSystem/Radio';
import { Select } from 'components/DesignSystem/Select';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';
import { ConfirmSaveModal, CustomizedFormErrorMessage } from 'components/common';

type FormProps = QueryParams;

interface Props {
    isShowModal: boolean;
    handleModalClose: () => void;
}

export const SaveSegment = ({ isShowModal, handleModalClose }: Props) => {
    const globalFilterData = useGlobalFilterDataStore((state) => state.globalFilterData);
    const { project, contract } = useProjectTreeStore();
    const projectId = project?.id;
    const contractId = contract?.id;
    const { id, versionToCompareId } = useParams<ProgramStatusUrlParams>();
    const hashcode = [id, versionToCompareId].filter((v): v is string => Boolean(v)).join(',');
    const { versionList: versionFromHashMap } = useHashmapConverter(hashcode);
    const latestVersionId = versionFromHashMap[0]?.versionDbId;

    let filters = useMemo(() => {
        if (!projectId || !contractId || !latestVersionId) return { filtersArray: [], flags: {} };

        const baseFilters = getFiltersData({
            [FilterNames.PROJECT_CARD_BOARD]: [projectId],
            [FilterNames.CONTRACT_CARD_BOARD]: [contractId],
            [FilterNames.VERSION_CARD_BOARD]: [latestVersionId],
            [FilterNames.ACTIVITY_TYPE_BOARD]: globalFilterData?.cardActivity,
            [FilterNames.FRAGNET_SEARCH]: globalFilterData?.milestoneFragnet,
            [FilterNames.TAG_CARD_BOARD]: globalFilterData?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
            [FilterNames.TAG_CARD_BOARD_EXCLUDE]: globalFilterData?.tags
                ?.filter((item) => item.isExclude)
                .map((i) => i.name),
            [FilterNames.WBS_SEARCH]: globalFilterData?.wbs,
            [FilterNames.ACTIVITY_CODES]: globalFilterData?.activityCodes
                ?.filter((item) => !item.isExclude)
                .map((i) => i.name),
            [FilterNames.ACTIVITY_CODES_EXCLUDE]: globalFilterData?.activityCodes
                ?.filter((item) => item.isExclude)
                .map((i) => i.name),
            [FilterNames.ASSIGNEE_CARD_BOARD]: globalFilterData?.assignee,
            [FilterNames.IMPORTANT_CARD_BOARD]: globalFilterData?.important,
            [FilterNames.URGENT_TYPE_CARD_BOARD]: globalFilterData?.urgent,
            [FilterNames.FRAGNET_MILESTONE_ID_COMPLETION_CARD_BOARD]: globalFilterData?.completionPlanSegment,
            [FilterNames.DOCLINK_CARD_BOARD]: globalFilterData?.hasAttachments,
            [FilterNames.LAG_CARD_BOARD]: globalFilterData?.lagLead,
            [FilterNames.TRACKED_CARD_BOARD]: globalFilterData?.trackedActivity,
            [FilterNames.FRAGNET_FORWARD_SEARCH]: globalFilterData?.activityForwardFragnet,
            [FilterNames.TYPE_CARD_BOARD]: globalFilterData?.cardType,
            [FilterNames.CLUSTER_CARD_BOARD]: globalFilterData?.cluster,
            [FilterNames.DURATION_CARD_BOARD]: globalFilterData?.duration,
            [FilterNames.TOTAL_FLOAT_CARD_BOARD]: globalFilterData?.float,
            [FilterNames.ACTIVITY_NAME_CARD_BOARD]: globalFilterData?.relatedActivity,
            [FilterNames.RESOURCE_NAME_CARD_BOARD]: globalFilterData?.resourceName,
            [FilterNames.RESOURCE_TYPE_CARD_BOARD]: globalFilterData?.resourceType,
            [FilterNames.SUBMITTED_COMPLETION_CARD_BOARD]: globalFilterData?.submittedCompletion,
            [FilterNames.UPDATED_USER_CARD_BOARD]: globalFilterData?.updatedBy,
            [FilterNames.WBS_LEVEL_CARD_BOARD]: globalFilterData?.wbsLevel,
            [FilterNames.STATUS_CARD_BOARD]: globalFilterData?.status,
        });

        const specialFiltersPayload = createSpecialFiltersPayload(globalFilterData);
        const specialFilters = specialFiltersPayload?.filters ?? [];
        const flags = specialFiltersPayload?.flags ?? {};

        return { filtersArray: [...baseFilters, ...specialFilters], flags };
    }, [contractId, globalFilterData, latestVersionId, projectId]);

    const pillsData = {
        cardActivity: globalFilterData?.cardActivity,
        assignee: globalFilterData?.assignee,
        important: globalFilterData?.important,
        urgent: globalFilterData?.urgent,
        completionPlanSegment: globalFilterData?.completionPlanSegment,
        hasAttachments: globalFilterData?.hasAttachments,
        lagLead: globalFilterData?.lagLead,
        trackedActivity: globalFilterData?.trackedActivity,
        activityForwardFragnet: globalFilterData?.activityForwardFragnet,
        cardType: globalFilterData?.cardType,
        cluster: globalFilterData?.cluster,
        duration: globalFilterData?.duration,
        float: globalFilterData?.float,
        relatedActivity: globalFilterData?.relatedActivity,
        resourceName: globalFilterData?.resourceName,
        submittedCompletion: globalFilterData?.submittedCompletion,
        updatedBy: globalFilterData?.updatedBy,
        wbsLevel: globalFilterData?.wbsLevel,
        status: globalFilterData?.status,
        resourceType: globalFilterData?.resourceType,
        milestoneFragnet: globalFilterData?.milestoneFragnet,
        wbs: globalFilterData?.wbs,
        finishDate: globalFilterData?.finishDate,
        startDate: globalFilterData?.startDate,
        baselineFinishDate: globalFilterData?.baselineFinishDate,
        baselineStartDate: globalFilterData?.baselineStartDate,
        hasComments: globalFilterData?.hasComments,
        riskIndicators: globalFilterData?.riskIndicators,
        searchByName: globalFilterData?.searchByName,
        activityCodes: globalFilterData?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
        activityCodesExclude: globalFilterData?.activityCodes?.filter((item) => item.isExclude).map((i) => i.name),
        tags: globalFilterData?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
        tagsExclude: globalFilterData?.tags?.filter((item) => item.isExclude).map((i) => i.name),
    };

    const { pillsList: pills } = useGlobalFiltersHook({ source: pillsData, latestVersionId });

    const { version } = useVersionStore();
    const { pathname, search } = useLocation();
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();
    const { sendEventWithDimensions } = useEventWithDimensions();
    const [confirmModal, setConfirmModal] = useState<boolean>(false);
    const [radioOption, setRadioOption] = useState<'create' | 'override'>('create');

    const { mutate: createSegment } = useMutationCreateSegment();
    const { mutate: updateSegment } = useMutationUpdateSegment();

    const { data: segments } = useQueryGetSegmentsList({ metaDataId: version?.id, contractId: contract?.id });

    const {
        watch,
        handleSubmit,
        control,
        formState: { errors },
        reset,
        setValue,
    } = useForm<FormProps>({
        values: {
            segmentId: null,
            contractId: contract?.id,
            entireProject: true,
            filter: {
                filters: filters.filtersArray,
            },
            name: generateFilterString(pills),
            metaDataId: version?.id,
            userGenerated: true,
            url: `${pathname.substring(1)}${search}`,
        },
    });

    const handleSave = () => {
        const payload = watch();
        const mutation = radioOption === 'create' ? createSegment : updateSegment;
        sendEventWithDimensions({ action: 'Save Segment', label: payload.name, category: 'Project Activities' });
        mutation(payload, {
            onSuccess: () => {
                queryClient.invalidateQueries('getSegmentsList').then(() => {
                    handleSnackBar(`Segment ${radioOption === 'create' ? 'created' : 'updated'}`, 'success');
                });
            },
            onError: (error) => {
                const message = 'Error saving segment';
                DEFAULT_ERROR({ error, message, handleError: handleSnackBar });
            },
            onSettled: () => {
                closeModal();
            },
        });
    };

    const closeModal = () => {
        handleModalClose();
        reset();
    };

    const options = useMemo(() => {
        return (
            segments?.map((item) => ({
                label: item.name,
                value: item.segmentId,
            })) || []
        );
    }, [segments]);

    return (
        <>
            <Modal
                open={isShowModal}
                onClose={closeModal}
                title={'Save current filter as Segment'}
                actions={
                    <>
                        <Button size={'large'} variant={'secondary'} onClick={closeModal}>
                            Cancel
                        </Button>

                        <Button
                            size={'large'}
                            variant={'primary'}
                            onClick={handleSubmit(() => setConfirmModal(true))}
                            disabled={radioOption === 'override' && !watch('segmentId')}
                        >
                            Save
                        </Button>
                    </>
                }
            >
                <div className={classes.saveSegment}>
                    <form onSubmit={handleSubmit(() => setConfirmModal(true))}>
                        {pills.length > 0 && (
                            <div className={classes.widgetFiltersWrapper}>
                                <div className={classes.widgetFiltersTitle}>Applied Filter:</div>
                                <div className={classes.widgetFiltersContent}>
                                    <PinboardWidgetPills key={'widgetFilters'} filters={pills} />
                                </div>
                            </div>
                        )}

                        <div className={classes.optionContainer}>
                            <Radio
                                onChange={() => {
                                    setRadioOption('create');
                                    setValue('name', '');
                                }}
                                value={'create'}
                                checked={radioOption === 'create'}
                                name={'create'}
                                label={'Create Segment'}
                            />

                            <Radio
                                onChange={() => setRadioOption('override')}
                                value={'override'}
                                checked={radioOption === 'override'}
                                name={'create'}
                                label={' Override Existing Segment'}
                            />
                        </div>
                        <div className={classes.selectTitle}>Enter segment name</div>
                        {radioOption === 'create' && (
                            <div className={classes.selectWrapper}>
                                <Controller
                                    render={({ field }) => (
                                        <TextInput
                                            {...field}
                                            placeholder={'Segment name...'}
                                            maxLength={30}
                                            onClear={() => {
                                                setValue('name', '');
                                            }}
                                        />
                                    )}
                                    name={'name'}
                                    control={control}
                                    rules={{
                                        validate: {
                                            required: (value) => value.length > 0 || 'Must select at least one tag',
                                        },
                                    }}
                                />
                                {errors.name && <CustomizedFormErrorMessage text={errors.name.message} />}
                            </div>
                        )}

                        {radioOption === 'override' && (
                            <div className={classes.selectWrapper}>
                                <Controller
                                    render={({ field }) => (
                                        <Select<ISelectOption<number>>
                                            {...field}
                                            value={options.find((item) => item.value === watch('segmentId')) || null}
                                            onChange={(value) => {
                                                if (value) {
                                                    field.onChange(value.value);
                                                    setValue('name', value.label);
                                                } else {
                                                    field.onChange(null);
                                                    setValue('name', '');
                                                }
                                            }}
                                            options={options}
                                            isClearable={true}
                                            placeholder={'Select segment...'}
                                        />
                                    )}
                                    name={'segmentId'}
                                    control={control}
                                />
                            </div>
                        )}
                    </form>
                </div>
            </Modal>

            <ConfirmSaveModal
                isShowModal={confirmModal}
                onHideModal={() => setConfirmModal(false)}
                onConfirm={() => {
                    setConfirmModal(false);
                    handleSave();
                }}
                actionButtonLabel={'Yes'}
            >
                Are you sure you want to save this segment?
            </ConfirmSaveModal>
        </>
    );
};
